import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { cn } from '@/lib/utils';
import { PageContainer, PageHeader } from '@/components/shared/layout';

import {
    useNotifications,
    useNotificationTemplates,
    useUnreadNotificationCount,
} from '../../api/notifications/use-notifications';
import { useNotificationRules } from '../../api/notifications/use-notification-rules';

import { NotificationList } from './components/NotificationList';
import { TemplateManager } from './components/TemplateManager';
import { AutomationRulesManager } from './components/AutomationRulesManager';
import { NotificationPagination } from './components/NotificationPagination';

interface NotificationCenterState {
    activeTab: 'notifications' | 'templates' | 'rules';
    filters: Record<string, any>;
    sortConfig: { key: string; direction: 'asc' | 'desc' };
    page: number;
    perPage: number;
}

const NotificationCenter: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();

    const canViewNotifications = hasPermission(
        ModulePermission.NOTIFICATIONS,
        PermissionAction.VIEW
    );
    const canViewTemplatesRules = hasPermission(
        ModulePermission.SYSTEM_SETTINGS,
        PermissionAction.VIEW
    );

    const visibleTabs = useMemo(() => {
        const tabs: Array<'notifications' | 'templates' | 'rules'> = [];
        if (canViewNotifications) tabs.push('notifications');
        if (canViewTemplatesRules) {
            tabs.push('templates');
            tabs.push('rules');
        }
        return tabs;
    }, [canViewNotifications, canViewTemplatesRules]);

    const defaultTab = useMemo(() => visibleTabs[0] ?? 'notifications', [visibleTabs]);

    const [state, setState] = useState<NotificationCenterState>({
        activeTab: defaultTab,
        filters: {},
        sortConfig: { key: 'created_at', direction: 'desc' },
        page: 1,
        perPage: 10,
    });

    const [isRefreshing, setIsRefreshing] = useState(false);

    const {
        data: notificationsResponse,
        isLoading: notificationsLoading,
        refetch: refetchNotifications,
    } = useNotifications({
        ...state.filters,
        sort_by: state.sortConfig.key,
        sort_order: state.sortConfig.direction,
        page: state.page,
        per_page: state.perPage,
    });

    const {
        data: templatesResponse,
        isLoading: templatesLoading,
        refetch: refetchTemplates,
    } = useNotificationTemplates({
        active_only: false,
        page: state.page,
        per_page: state.perPage,
    });

    const {
        data: rulesResponse,
        isLoading: rulesLoading,
        refetch: refetchRules,
    } = useNotificationRules({
        active_only: false,
        page: state.page,
        per_page: state.perPage,
    });

    const { data: unreadCount, refetch: refetchUnreadCount } = useUnreadNotificationCount();

    const notifications = Array.isArray(notificationsResponse?.data)
        ? notificationsResponse.data
        : [];
    const templates = Array.isArray(templatesResponse?.data) ? templatesResponse.data : [];
    const rules = Array.isArray(rulesResponse?.data) ? rulesResponse.data : [];

    useEffect(() => {
        if (visibleTabs.length > 0 && !visibleTabs.includes(state.activeTab)) {
            setState(prev => ({ ...prev, activeTab: defaultTab }));
        }
    }, [visibleTabs, defaultTab, state.activeTab]);

    const handleTabChange = useCallback((tab: string) => {
        if (tab === 'notifications' || tab === 'templates' || tab === 'rules') {
            setState(prev => ({
                ...prev,
                activeTab: tab as 'notifications' | 'templates' | 'rules',
                page: 1,
            }));
        }
    }, []);

    const handleFilterChange = useCallback((newFilters: Record<string, any>) => {
        setState(prev => ({ ...prev, filters: newFilters, page: 1 }));
    }, []);

    const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
        setState(prev => ({ ...prev, sortConfig: { key, direction } }));
    }, []);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                refetchNotifications(),
                refetchTemplates(),
                refetchRules(),
                refetchUnreadCount(),
            ]);
        } catch {
            enqueueSnackbar('Failed to refresh data', { variant: 'error' });
        } finally {
            setIsRefreshing(false);
        }
    }, [refetchNotifications, refetchTemplates, refetchRules, refetchUnreadCount, enqueueSnackbar]);

    const tabCount = visibleTabs.length;

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Notifications"
                actions={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        aria-label="Refresh notifications"
                        title="Refresh notifications"
                    >
                        <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
                    </Button>
                }
            />

            {/* Tabs fill the rest of the page */}
            <Tabs
                value={visibleTabs.includes(state.activeTab) ? state.activeTab : defaultTab}
                onValueChange={handleTabChange}
                className="flex-1 flex flex-col overflow-hidden min-h-0"
            >
                {/* Tab selectors */}
                <div className="border-b px-6 shrink-0">
                    <TabsList
                        className={`grid w-full max-w-lg ${
                            tabCount === 1
                                ? 'grid-cols-1'
                                : tabCount === 2
                                  ? 'grid-cols-2'
                                  : 'grid-cols-3'
                        }`}
                    >
                        {canViewNotifications && (
                            <TabsTrigger value="notifications" className="flex items-center gap-2">
                                Notifications
                                {typeof unreadCount === 'number' && unreadCount > 0 && (
                                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                                        {unreadCount}
                                    </span>
                                )}
                            </TabsTrigger>
                        )}
                        {canViewTemplatesRules && (
                            <TabsTrigger value="templates">Templates</TabsTrigger>
                        )}
                        {canViewTemplatesRules && (
                            <TabsTrigger value="rules">Workflows & Triggers</TabsTrigger>
                        )}
                    </TabsList>
                </div>

                {/* Notifications tab */}
                {canViewNotifications && (
                    <TabsContent
                        value="notifications"
                        className="flex-1 flex flex-col overflow-hidden m-0"
                    >
                        <div className="flex-1 overflow-auto p-4">
                            <NotificationList
                                notifications={notifications}
                                filters={state.filters}
                                sortConfig={state.sortConfig}
                                isLoading={notificationsLoading}
                                onFilterChange={handleFilterChange}
                                onSortChange={handleSortChange}
                                onRefresh={handleRefresh}
                            />
                        </div>
                        {notificationsResponse?.meta && (
                            <div className="shrink-0 border-t">
                                <NotificationPagination
                                    meta={notificationsResponse.meta}
                                    onPageChange={page => setState(prev => ({ ...prev, page }))}
                                    onPerPageChange={perPage =>
                                        setState(prev => ({ ...prev, perPage, page: 1 }))
                                    }
                                />
                            </div>
                        )}
                    </TabsContent>
                )}

                {/* Templates tab */}
                {canViewTemplatesRules && (
                    <TabsContent
                        value="templates"
                        className="flex-1 flex flex-col overflow-hidden m-0"
                    >
                        <div className="flex-1 overflow-auto">
                            <TemplateManager
                                templates={templates}
                                isLoading={templatesLoading}
                                onRefresh={handleRefresh}
                            />
                        </div>
                        {templatesResponse?.meta && (
                            <div className="shrink-0 border-t">
                                <NotificationPagination
                                    meta={templatesResponse.meta}
                                    onPageChange={page => setState(prev => ({ ...prev, page }))}
                                    onPerPageChange={perPage =>
                                        setState(prev => ({ ...prev, perPage, page: 1 }))
                                    }
                                />
                            </div>
                        )}
                    </TabsContent>
                )}

                {/* Workflows & Triggers tab */}
                {canViewTemplatesRules && (
                    <TabsContent value="rules" className="flex-1 flex flex-col overflow-hidden m-0">
                        <div className="flex-1 overflow-auto">
                            <AutomationRulesManager
                                rules={rules}
                                templates={templates}
                                isLoading={rulesLoading}
                                onRefresh={handleRefresh}
                            />
                        </div>
                        {rulesResponse?.meta && (
                            <div className="shrink-0 border-t">
                                <NotificationPagination
                                    meta={rulesResponse.meta}
                                    onPageChange={page => setState(prev => ({ ...prev, page }))}
                                    onPerPageChange={perPage =>
                                        setState(prev => ({ ...prev, perPage, page: 1 }))
                                    }
                                />
                            </div>
                        )}
                    </TabsContent>
                )}
            </Tabs>
        </PageContainer>
    );
};

export default NotificationCenter;
